import React, { useEffect, useState } from 'react'
import {Link, Redirect, useParams} from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'

import { toast } from 'react-toastify'
import * as yup from 'yup'
import { Helmet } from 'react-helmet'
import pick from 'lodash/pick'
import mapValues from 'lodash/mapValues';

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'

const ProductOptionDetailsEditContainer = ({ children }) => {
    const { id } = useParams();

    return (
        <div className="form-container">
            <Heading name="Edit Product Option Detail">
                <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                    <Link
                        className="btn btn-outline-primary"
                        to={`/options/${id}/details`}
                        data-testid="back-to-product-option-details"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
                        Back to Product Option Details
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const ProductOptionDetailsEdit = ({ ProductOptionDetailsForm }) => {
    const { client, getResource } = useClient()
    const { id, detailId } = useParams();
    const productOptionDetailsPage = `/options/${id}/details`
    const pathname = `/product-option-details/${detailId}`;

    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()

    const [productOptionDetails, setProductOptionDetails] = useState()

    const fetchProductOptionDetails = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'product-option-details', {
                id: pathname,
            })

            setProductOptionDetails(data)
            setLoading(false)

            if (response.status === 404) {
                setRedirect(productOptionDetailsPage)
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect(productOptionDetailsPage)
        }
    }

    useEffect(() => {
        fetchProductOptionDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const schema = getSchemaFromResource(getResource('product-option-details')).shape({
        variantOptionIdentifier: yup.string().required(),
        productOption: yup.string().required(),
    })

    const onSubmit = async (data, actions) => {
        const pickedData = pick(data, ['variantOptionIdentifier', 'location', 'description', 'bestSeller', 'expressDelivery', 'justAdded', 'excludedFromSwatchRequests'])
        const productOptionDetailsData = mapValues(pickedData, (value) => (value === "" ? null : value));

        try {
            const { data } = await client(UPDATE, 'product-option-details', {
                id: pathname,
                data: productOptionDetailsData,
            })

            if (data.violations && data.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                for (const error of data.violations) {
                    actions.setFieldError(error.propertyPath, error.message);
                }
            } else {
                fetchProductOptionDetails()
                actions.setSubmitting(false)
                toast.success('Product Option Detail successfully updated!')
            }
        } catch (e) {
            console.error(e)
            if (e?.error?.body?.violations && e.error.body.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                for (const error of e.error?.body?.violations ?? []) {
                    actions.setFieldError(error.propertyPath, error.message);
                }
            }
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            actions.setSubmitting(false)
            actions.setValues(productOptionDetailsData)
        }
    }

    if (loading) {
        return (
            <ProductOptionDetailsEditContainer>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </ProductOptionDetailsEditContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url={`/options/${id}/details`}
                message="The product option detail you're looking for could not be found"
                buttonLabel="Go to Product Option Details"
            />
        )
    }

    return (
        <ProductOptionDetailsEditContainer>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>Product Option Details | Edit | Peracto</title>
            </Helmet>

            <ProductOptionDetailsForm values={productOptionDetails} onSubmit={onSubmit} schema={schema} />
        </ProductOptionDetailsEditContainer>
    )
}

export default ProductOptionDetailsEdit
